import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Brand } from "../../../../context/BrandContext";
import { ChevronLeft, X } from "lucide-react";

type Props = {
  isOpen: boolean;
  brands: Brand[];
  anchorRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;

  variant?: "desktop" | "mobile";
  onBack?: () => void;
};

type RangeKey = "A-E" | "F-J" | "K-P" | "Q-Z" | "#";

const RANGES: { key: RangeKey; label: string; from: string; to: string }[] = [
  { key: "A-E", label: "A to E", from: "A", to: "E" },
  { key: "F-J", label: "F to J", from: "F", to: "J" },
  { key: "K-P", label: "K to P", from: "K", to: "P" },
  { key: "Q-Z", label: "Q to Z", from: "Q", to: "Z" },
];

function normalizeFirstLetter(name: string) {
  const trimmed = (name || "").trim();
  if (!trimmed) return "#";
  const first = trimmed[0].toUpperCase();
  return /^[A-Z]$/.test(first) ? first : "#";
}

function getRangeKey(letter: string): RangeKey {
  if (!/^[A-Z]$/.test(letter)) return "#";
  for (const r of RANGES) {
    if (letter >= r.from && letter <= r.to) return r.key;
  }
  return "#";
}

function useOnClickOutside(
  refs: React.RefObject<HTMLElement | null>[],
  handler: () => void,
  when: boolean
) {
  useEffect(() => {
    if (!when) return;

    const listener = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;

      for (const ref of refs) {
        const el = ref.current;
        if (el && el.contains(target)) return;
      }
      handler();
    };

    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [refs, handler, when]);
}

const BrandsMenu: React.FC<Props> = ({
  isOpen,
  brands,
  anchorRef,
  onClose,
  variant = "desktop",
  onBack,
}) => {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [query, setQuery] = useState("");

  // click-outside + ESC only for desktop flyout
  useOnClickOutside(
    [menuRef, anchorRef],
    onClose,
    isOpen && variant === "desktop"
  );

  useEffect(() => {
    if (!isOpen || variant !== "desktop") return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose, variant]);

  useEffect(() => {
    if (!isOpen) setQuery("");
  }, [isOpen]);

  const columns = useMemo(() => {
    const q = query.trim().toLowerCase();

    const filtered = (brands || []).filter((b) => {
      const name = (b.name || "").toLowerCase();
      return q ? name.includes(q) : true;
    });

    const sorted = [...filtered].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, {
        sensitivity: "base",
      })
    );

    const map = new Map<RangeKey, Brand[]>();
    (["A-E", "F-J", "K-P", "Q-Z", "#"] as RangeKey[]).forEach((k) =>
      map.set(k, [])
    );

    for (const b of sorted) {
      const letter = normalizeFirstLetter(b.name);
      const key = getRangeKey(letter);
      map.get(key)!.push(b);
    }

    const result: { key: RangeKey; title: string; brands: Brand[] }[] = [];

    for (const r of RANGES) {
      const arr = map.get(r.key)!;
      if (arr.length > 0)
        result.push({ key: r.key, title: r.label, brands: arr });
    }

    const hashArr = map.get("#")!;
    if (hashArr.length > 0)
      result.push({ key: "#", title: "#", brands: hashArr });

    return result;
  }, [brands, query]);

  if (!isOpen) return null;

  // ✅ MOBILE variant (render INSIDE the existing panel)
  if (variant === "mobile") {
    return (
      <div className="pb-2">
        <div className="flex items-center justify-between px-1 pb-3">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-2 px-2 py-2 rounded-full hover:bg-black/5 transition text-[#5c2f2f]"
          >
            <ChevronLeft size={18} />
            <span className="text-xs font-bold uppercase tracking-widest">
              Back
            </span>
          </button>

          <button
            type="button"
            onClick={onClose}
            className="text-[#5c2f2f] p-2 rounded-full hover:bg-black/5 transition"
            aria-label="Close brands"
          >
            <X size={18} />
          </button>
        </div>

        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search brands..."
          className="
            w-full
            px-3 py-2
            rounded-xl
            border border-[#f2cccc]
            bg-white/80
            text-[#5c2f2f]
            placeholder:text-[#7a4343]
            focus:outline-none focus:ring-2 focus:ring-[#f2cccc]
          "
        />

        <div
          className="
            mt-3
            max-h-[55vh]
            overflow-y-auto
            overscroll-contain
            pr-1
            [-webkit-overflow-scrolling:touch]
            touch-pan-y
          "
        >
          {columns.length === 0 ? (
            <div className="px-2 py-4 text-sm text-[#7a4343]">
              No matching brands.
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {columns.map((group) => (
                <div
                  key={group.key}
                  className="rounded-xl bg-white/60 border border-white/60 overflow-hidden"
                >
                  <div className="px-3 py-2 text-xs font-bold uppercase tracking-widest text-[#7a4343]">
                    {group.title}{" "}
                    <span className="font-normal">({group.brands.length})</span>
                  </div>

                  <div className="px-2 pb-2">
                    {group.brands.map((b) => (
                      <Link
                        key={b.id}
                        to={`/products?brand=${b.id}`}
                        onClick={onClose}
                        className="
                          block
                          px-3 py-2
                          rounded-lg
                          text-[#5c2f2f]
                          font-semibold
                          uppercase tracking-wide
                          text-sm
                          hover:bg-[#ffe9e9]
                          transition
                        "
                      >
                        {b.name}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ✅ DESKTOP mega menu
  return (
    <div
      ref={menuRef}
      className="
        absolute top-full mt-3 left-1/2 -translate-x-1/2
        z-[9999]
        w-[980px] max-w-[95vw]
        rounded-2xl
        bg-white/30
        backdrop-blur-2xl
        shadow-[0_30px_90px_rgba(0,0,0,0.55)]
        border border-white/30
        overflow-hidden
      "
      role="menu"
      aria-label="Brands menu"
    >
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/20">
        <p
          className="
            text-sm font-bold tracking-[0.3em] uppercase text-white
            [text-shadow:
              0_2px_6px_rgba(0,0,0,0.9),
              0_0_18px_rgba(255,255,255,0.35),
              0_0_30px_rgba(255,255,255,0.25)
            ]
          "
        >
          Brands
        </p>

        <button
          type="button"
          onClick={onClose}
          aria-label="Close brands menu"
          className="
            h-9 w-9
            grid place-items-center
            rounded-full
            text-white
            hover:bg-white/15
            [text-shadow:0_2px_10px_rgba(0,0,0,0.9)]
            transition
          "
        >
          <span className="text-2xl leading-none">×</span>
        </button>
      </div>

      <div className="px-5 py-5">
        {columns.length === 0 ? (
          <p className="text-sm text-white/95 [text-shadow:0_2px_10px_rgba(0,0,0,0.9)]">
            No brands yet.
          </p>
        ) : (
          <div className="grid gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            {columns.map((col) => {
              const hero = col.brands.find((b) => !!b.logo_url)?.logo_url || null;

              return (
                <div key={col.key} className="min-w-0">
                  <div
                    className="
                      relative h-[110px]
                      rounded-xl overflow-hidden
                      border border-white/25
                      shadow-[0_18px_55px_rgba(0,0,0,0.35)]
                    "
                  >
                    {hero ? (
                      <img
                        src={hero}
                        alt={`${col.title} brands`}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="h-full w-full bg-gradient-to-br from-[#fbe7e7]/55 to-[#f7dada]/35" />
                    )}

                    <div className="absolute inset-0 bg-black/20" />

                    <div className="absolute bottom-2 left-3">
                      <p
                        className="
                          text-xs font-extrabold tracking-[0.35em] uppercase text-white
                          [text-shadow:0_3px_12px_rgba(0,0,0,0.95)]
                        "
                      >
                        {col.title}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-col gap-1.5">
                    {col.brands.map((brand) => (
                      <Link
                        key={brand.id}
                        to={`/products?brand=${brand.id}`}
                        onClick={onClose}
                        role="menuitem"
                        className="
                          group
                          w-fit max-w-full
                          text-white text-sm font-semibold
                          uppercase tracking-wide
                          [text-shadow:0_3px_12px_rgba(0,0,0,0.95)]
                        "
                      >
                        <span className="inline-block transition-transform duration-200 ease-out group-hover:scale-[1.08]">
                          {brand.name}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="pointer-events-none h-6 bg-gradient-to-b from-transparent to-black/20" />
    </div>
  );
};

export default BrandsMenu;
